import { create } from 'zustand';
import type { ReviewDto, ReviewStatus } from '@ecom/contracts';
import { adminApi, errorMessage, withSession } from '@/lib/api-client';
import { settleModerated } from '@/lib/queue-rows';
import { useWorkspaceStore } from './workspace.store';

/**
 * Review moderation.
 *
 * Reviews are held as PENDING until someone acts: an unmoderated review box on
 * a public storefront becomes a spam target within days. Approving or rejecting
 * recomputes the product's rating from approved reviews only, server-side.
 */

export interface ReviewFilters {
  search: string;
  statuses: ReviewStatus[];
  /** One product at a time — "why is this one rated badly" is the question. */
  productId: string;
  /** 1–5 as typed. */
  minRating: string;
  maxRating: string;
}

export const EMPTY_REVIEW_FILTERS: ReviewFilters = {
  search: '',
  statuses: [],
  productId: '',
  minRating: '',
  maxRating: '',
};

/** What a selection's publish or reject came to: how many went through, and which the API refused and why. */
export interface ReviewBulkOutcome {
  done: number;
  failed: { id: string; label: string; message: string }[];
}

type Decision = Extract<ReviewStatus, 'APPROVED' | 'REJECTED'>;

interface ReviewsState {
  items: ReviewDto[];
  nextCursor: string | null;
  loading: boolean;
  loadingMore: boolean;
  error: string | null;
  filters: ReviewFilters;
  total: number | null;
  /** The filtered product's name, for its chip. Null while unknown. */
  productName: string | null;
  /** Reviews with a change in flight: each card waits on its own, not on its neighbours. */
  busyIds: string[];
  /** A selection being published or rejected. */
  bulkSaving: boolean;

  load: (filters?: Partial<ReviewFilters>) => Promise<void>;
  loadMore: () => Promise<void>;
  moderate: (id: string, status: Decision) => Promise<boolean>;
  /**
   * The same decision for a selection, one review at a time — like the orders
   * list's bulk move. The API may refuse one and accept the rest, so the answer
   * says which went through and why the others did not.
   */
  moderateMany: (ids: string[], status: Decision) => Promise<ReviewBulkOutcome>;
  /** The store's public answer (R8); null takes it down. */
  reply: (id: string, body: string | null) => Promise<boolean>;
}

const PAGE_SIZE = 20;

export const useReviewsStore = create<ReviewsState>((set, get) => {
  const busy = (id: string, on: boolean) =>
    set((state) => ({
      busyIds: on ? [...state.busyIds, id] : state.busyIds.filter((entry) => entry !== id),
    }));

  return {
    items: [],
    nextCursor: null,
    loading: false,
    loadingMore: false,
    error: null,
    // Opens on the queue that needs attention, not on everything ever written.
    filters: { ...EMPTY_REVIEW_FILTERS, statuses: ['PENDING'] },
    total: null,
    productName: null,
    busyIds: [],
    bulkSaving: false,

    async load(filters) {
      const previous = get();
      const next = { ...previous.filters, ...filters };
      set({ loading: true, error: null, filters: next });

      try {
        const page = await withSession(() => adminApi.reviews.list(toQuery(next, PAGE_SIZE)));
        // Every row of a product's reviews carries its name; only an empty list
        // has to ask for it.
        const known =
          page.items[0]?.product.name ??
          (previous.filters.productId === next.productId ? previous.productName : null);
        set({
          items: page.items,
          nextCursor: page.meta.nextCursor,
          total: page.meta.total ?? null,
          loading: false,
          productName: next.productId ? known : null,
        });

        if (next.productId && !known) {
          const name = await productNameOf(next.productId);
          if (get().filters.productId === next.productId) set({ productName: name });
        }
      } catch (error) {
        set({ loading: false, error: errorMessage(error, 'Could not load reviews.') });
      }
    },

    async loadMore() {
      const { nextCursor, loadingMore, filters, items } = get();
      if (!nextCursor || loadingMore) return;

      set({ loadingMore: true });

      try {
        const page = await withSession(() =>
          adminApi.reviews.list({ ...toQuery(filters, PAGE_SIZE), cursor: nextCursor }),
        );
        set({ items: [...items, ...page.items], nextCursor: page.meta.nextCursor, loadingMore: false });
      } catch (error) {
        set({ loadingMore: false, error: errorMessage(error, 'Could not load more reviews.') });
      }
    },

    async moderate(id, status) {
      busy(id, true);
      set({ error: null });

      try {
        const updated = await withSession(() => adminApi.reviews.moderate(id, { status }));
        set((state) => settle(state, updated));
        // The menu's count of reviews waiting has just changed.
        void useWorkspaceStore.getState().refreshAttention();
        return true;
      } catch (error) {
        set({ error: errorMessage(error, 'Could not moderate this review.') });
        return false;
      } finally {
        busy(id, false);
      }
    },

    async moderateMany(ids, status) {
      set({ bulkSaving: true, error: null });
      const failed: ReviewBulkOutcome['failed'] = [];
      let done = 0;

      // One at a time: each recomputes its product's rating, and a burst of
      // parallel writes is how a small server falls over.
      for (const id of ids) {
        const row = get().items.find((review) => review.id === id);
        // Already there: the API refuses a decision that changes nothing.
        if (row?.status === status) continue;

        try {
          const updated = await withSession(() => adminApi.reviews.moderate(id, { status }));
          done += 1;
          set((state) => settle(state, updated));
        } catch (error) {
          failed.push({
            id,
            label: row ? `${row.authorName} · ${row.product.name}` : id,
            message: errorMessage(error, 'Refused.'),
          });
        }
      }

      set({ bulkSaving: false });
      void useWorkspaceStore.getState().refreshAttention();
      return { done, failed };
    },

    async reply(id, body) {
      busy(id, true);
      set({ error: null });

      try {
        const updated = await withSession(() =>
          body ? adminApi.reviews.reply(id, body) : adminApi.reviews.removeReply(id),
        );
        set((state) => ({ items: state.items.map((row) => (row.id === id ? updated : row)) }));
        return true;
      } catch (error) {
        set({ error: errorMessage(error, 'Could not save the reply.') });
        return false;
      } finally {
        busy(id, false);
      }
    },
  };
});

/** A moderated review leaves a view that no longer holds its status; elsewhere it updates in place. */
function settle(state: ReviewsState, updated: ReviewDto): Partial<ReviewsState> {
  return settleModerated(state.items, state.total, state.filters.statuses, updated);
}

/** A product's name by id, for the filter chip when no row on screen carries it. Null when it cannot be read. */
async function productNameOf(id: string): Promise<string | null> {
  try {
    return (await withSession(() => adminApi.catalog.product(id))).name;
  } catch {
    return null;
  }
}

function toQuery(filters: ReviewFilters, limit: number) {
  return {
    limit,
    search: filters.search || undefined,
    status: filters.statuses.length > 0 ? filters.statuses : undefined,
    productId: filters.productId || undefined,
    minRating: filters.minRating ? Number(filters.minRating) : undefined,
    maxRating: filters.maxRating ? Number(filters.maxRating) : undefined,
  };
}
